/**
 * @file pageHeaders.ts
 * @brief Route-based page titles and subtitles for the app header bar.
 */

export interface PageHeaderConfig {
  title: string;
  subtitle?: string;
}

const PAGE_HEADERS: Array<{ match: (path: string) => boolean; config: PageHeaderConfig }> = [
  {
    match: (path) => path === '/' || path === '/dashboard',
    config: { title: 'Dashboard' },
  },
  {
    match: (path) => path === '/tasks',
    config: {
      title: 'My Tasks',
      subtitle: 'View and manage your assigned work',
    },
  },
  {
    match: (path) => path.startsWith('/tasks/'),
    config: { title: 'Task Details' },
  },
  {
    match: (path) => path === '/delegated',
    config: {
      title: 'Delegated Tasks',
      subtitle: 'Manage one-on-one task assignments',
    },
  },
  {
    match: (path) => path === '/projects',
    config: {
      title: 'Projects',
      subtitle: 'Track progress across your projects',
    },
  },
  {
    match: (path) => path.startsWith('/projects/'),
    config: { title: 'Project Details' },
  },
  {
    match: (path) => path === '/calendar',
    config: {
      title: 'Calendar',
      subtitle: 'Tasks, deadlines, and leave at a glance',
    },
  },
  {
    match: (path) => path === '/team',
    config: {
      title: 'Team',
      subtitle: 'Monitor workload and team performance',
    },
  },
  {
    match: (path) => path.startsWith('/team/'),
    config: { title: 'Team Member' },
  },
  {
    match: (path) => path === '/departments',
    config: {
      title: 'Department Performance',
      subtitle: 'Monitor department-wise tasks and productivity metrics',
    },
  },
  {
    match: (path) => path === '/reviews',
    config: {
      title: 'Reviews & Approval',
      subtitle: 'Performance reviews and pending approvals',
    },
  },
  {
    match: (path) => path === '/users',
    config: {
      title: 'Users & Access',
      subtitle: 'Manage users, departments, and permissions',
    },
  },
  {
    match: (path) => path === '/settings',
    config: {
      title: 'Settings',
      subtitle: 'Preferences and application configuration',
    },
  },
];

/**
 * Resolves the page header for the current route.
 */
export function getPageHeader(pathname: string): PageHeaderConfig {
  const entry = PAGE_HEADERS.find(({ match }) => match(pathname));
  return entry?.config ?? { title: 'Task Management' };
}
